import { useLayoutEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { colorShade } from '../utils/style';

interface SliderProps { size?: SliderSize; orientation?: SliderOrientation; min?: number; max?: number; isRange?: boolean; isDisabled?: boolean; initAnimation?: boolean; className?: any; }

type SliderMarkedProps = { step?: number; marks?: SliderMarkProps[]; } | { step: 'marks'; marks: (SliderMarkProps & { color?: string })[]; }
type SliderRangeProps = { isRange?: false; value?: number; onChange(value: number): void; customLabel?(value: number): string; }
    | { isRange: true; value?: [number, number]; onChange(value: [number, number]): void; customLabel?(value: [number, number]): string; };

type SliderMarkProps = { value: number; label: string | JSX.Element; }
export enum SliderSize { SMALL, MEDIUM, LARGE, }
export enum SliderOrientation { HORIZONTAL, VERTICAL, }

const sizeOffsets = { [SliderSize.SMALL]: 5, [SliderSize.MEDIUM]: 10, [SliderSize.LARGE]: 15 };
const mouseOrientations = { [SliderOrientation.HORIZONTAL]: 'clientX' as 'clientX', [SliderOrientation.VERTICAL]: 'clientY' as 'clientY' };
const positionOrientations = { [SliderOrientation.HORIZONTAL]: 'x' as 'x', [SliderOrientation.VERTICAL]: 'y' as 'y' };
const dimensionOrientations = { [SliderOrientation.HORIZONTAL]: 'width' as 'width', [SliderOrientation.VERTICAL]: 'height' as 'height' };
const translateOrientations = { [SliderOrientation.HORIZONTAL]: 'translateX' as 'translateX', [SliderOrientation.VERTICAL]: 'translateY' as 'translateY' };
const handleToValueName = { handleMax: 'handleMaxValue' as 'handleMaxValue', handleMin: 'handleMinValue' as 'handleMinValue' };

export default function Slider({ value, size = SliderSize.MEDIUM, orientation = SliderOrientation.HORIZONTAL, isRange = false, min = 0, max = 100, step = 1, initAnimation = false, onChange, marks = [], customLabel, className }: SliderProps & SliderRangeProps & SliderMarkedProps) {
    const progress = useRef({ currentHandle: 'handleMax' as 'handleMax' | 'handleMin', handleMin: 0, handleMax: 0, handleMinValue: 0, handleMaxValue: 0, marksByValue: {} as { [key: number]: number } });
    const animation = useRef({ animationFrame: null as any, handleMin: 0, handleMax: 0 });

    const selfRef = useRef<HTMLElement>(null);
    const valueTransformRef = useRef<HTMLDivElement>(null);
    const valueRef = useRef<HTMLDivElement>(null);
    const barRef = useRef<HTMLDivElement>(null);

    if (value !== undefined && selfRef.current) {
        if (typeof value === 'number' && value !== progress.current.handleMaxValue) { initValues(); initPosition(); }
        else if (typeof value === 'object' && (value as any).length === 2 && (((value as any)[0] !== progress.current.handleMinValue) || ((value as any)[1] !== progress.current.handleMaxValue))) { initValues(); initPosition(); }
    }

    useLayoutEffect(() => {
        initValues();
        initPosition();
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return () => {
            cancelAnimationFrame(animation.current.animationFrame);
            window.removeEventListener('mousemove', calcPosition);
            window.removeEventListener('mouseup', onMouseUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [min, max]);

    function initValues() {
        if (typeof value === 'number') { progress.current.handleMaxValue = value; progress.current.handleMinValue = min; }
        else if (typeof value === 'object') [progress.current.handleMinValue, progress.current.handleMaxValue] = value;
        else progress.current.handleMinValue = progress.current.handleMaxValue = min;

        if (step === 'marks') {
            progress.current.marksByValue = (marks as (SliderMarkProps & { color?: string })[]).reduce((p, c, i) => { p[c.value] = i; return p; }, {} as { [key: number]: number });
            if (!progress.current.marksByValue[progress.current.handleMaxValue]) progress.current.handleMaxValue = marks[0]?.value || 0;
        }
    }

    function initPosition() {
        const selfRect = (selfRef.current as HTMLElement).getBoundingClientRect();
        const maxLength = selfRect[dimensionOrientations[orientation]] - sizeOffsets[size] * 2;

        progress.current.handleMax = Math.min(Math.max((progress.current.handleMaxValue - min) * (maxLength / (max - min)), 0), maxLength);
        progress.current.handleMin = isRange ? Math.min(Math.max((progress.current.handleMinValue - min) * (maxLength / (max - min)), 0), maxLength) : 0;

        animate(!initAnimation);
    }

    function calcPosition(e: MouseEvent) {
        const selfRect = (selfRef.current as HTMLElement).getBoundingClientRect();
        const offsetSize = sizeOffsets[size];
        const maxLength = selfRect[dimensionOrientations[orientation]] - offsetSize * 2;

        let currentPosition = e[mouseOrientations[orientation]] - selfRect[positionOrientations[orientation]] - offsetSize;
        if (orientation === SliderOrientation.VERTICAL) currentPosition = (selfRect.height - offsetSize * 2) - currentPosition;

        let currentValue = min + (currentPosition * (max - min)) / (selfRect[dimensionOrientations[orientation]] - offsetSize * 2);
        if (step !== 'marks') {
            const steps = Math.round(currentValue / step);
            currentValue = Math.min(Math.max(steps * step, min), max);
        } else {
            let currentStepIndex = progress.current.marksByValue[progress.current[handleToValueName[progress.current.currentHandle]]];
            while (true) {
                if (Math.abs(marks[currentStepIndex + 1]?.value - currentValue) < Math.abs(marks[currentStepIndex].value - currentValue)) { currentStepIndex += 1 }
                else if (Math.abs(marks[currentStepIndex - 1]?.value - currentValue) < Math.abs(marks[currentStepIndex].value - currentValue)) currentStepIndex -= 1;
                else { currentValue = marks[currentStepIndex].value; break; }
            }
        }

        const previousValue = progress.current[handleToValueName[progress.current.currentHandle]];

        progress.current[handleToValueName[progress.current.currentHandle]] = currentValue;
        progress.current[progress.current.currentHandle] = Math.min(Math.max((progress.current[handleToValueName[progress.current.currentHandle]] - min) * (maxLength / (max - min)), 0), maxLength);

        if (progress.current.handleMaxValue < progress.current.handleMinValue) {
            [progress.current.handleMaxValue, progress.current.handleMinValue] = [progress.current.handleMinValue, progress.current.handleMaxValue];
            [progress.current.handleMax, progress.current.handleMin] = [progress.current.handleMin, progress.current.handleMax];
            progress.current.currentHandle = progress.current.currentHandle === 'handleMax' ? 'handleMin' : 'handleMax';
        }

        if (previousValue !== currentValue) (onChange as any)(isRange ? [progress.current.handleMinValue, progress.current.handleMaxValue] : progress.current.handleMaxValue);

        animate();
    }

    function animate(instant?: boolean) {
        if (animation.current.animationFrame && !instant) return;

        //TODO try canvas or svg
        const handleAnimation = (time: any, instant?: boolean) => {
            const offsetSize = sizeOffsets[size];
            const animationOver = (Math.abs(progress.current.handleMax - animation.current.handleMax) < 1 && Math.abs(progress.current.handleMin - animation.current.handleMin) < 1) || instant;

            if (animationOver) [animation.current.handleMin, animation.current.handleMax] = [progress.current.handleMin, progress.current.handleMax];
            else { animation.current.handleMax += (progress.current.handleMax - animation.current.handleMax) / 5; animation.current.handleMin += (progress.current.handleMin - animation.current.handleMin) / 5; }

            (barRef.current as HTMLElement).style.transform = `${translateOrientations[orientation]}(${orientation === SliderOrientation.VERTICAL ? '-' : ''}${Math.floor(animation.current.handleMin)}px)`;
            (barRef.current as HTMLElement).style[dimensionOrientations[orientation]] = `${Math.floor(animation.current.handleMax) - Math.floor(animation.current.handleMin) + offsetSize * 2}px`;
            if (step === 'marks' && !isRange) (barRef.current as HTMLElement).style.backgroundColor = ((marks as (SliderMarkProps & { color?: string })[]))[progress.current.marksByValue[progress.current.handleMaxValue]].color || '';
            (barRef.current as HTMLElement).style.opacity = Math.floor(progress.current.handleMaxValue) === 0 && min === 0 ? '0.6' : '1';

            const deicmalPoints = (step.toString().split('.')[1] || '').length;

            if (isRange) {
                (valueTransformRef.current as HTMLElement).style.transform = `${translateOrientations[orientation]}(${orientation === SliderOrientation.VERTICAL ? '-' : ''}${(Math.floor(animation.current.handleMax) + Math.floor(animation.current.handleMin)) / 2 + offsetSize}px) ${orientation === SliderOrientation.VERTICAL ? '' : 'translateX(-50%)'}`;
                (valueRef.current as HTMLElement).innerHTML = customLabel ? customLabel([progress.current.handleMinValue, progress.current.handleMaxValue] as any) : `${(progress.current.handleMinValue).toFixed(deicmalPoints)} - ${(progress.current.handleMaxValue).toFixed(deicmalPoints)}`;
            } else {
                (valueTransformRef.current as HTMLElement).style.transform = `${translateOrientations[orientation]}(${orientation === SliderOrientation.VERTICAL ? '-' : ''}${Math.floor(animation.current.handleMax + offsetSize)}px) ${orientation === SliderOrientation.VERTICAL ? '' : 'translateX(-50%)'}`;
                (valueRef.current as HTMLElement).innerHTML = customLabel ? customLabel(progress.current.handleMaxValue as any) : `${(progress.current.handleMaxValue).toFixed(deicmalPoints)}`;
            }

            if (animationOver) { animation.current.animationFrame = null; return; }
            animation.current.animationFrame = requestAnimationFrame(handleAnimation);
        }

        if (instant) {
            cancelAnimationFrame(animation.current.animationFrame);
            handleAnimation(null, true);
        } else {
            animation.current.animationFrame = requestAnimationFrame(handleAnimation);
        }
    }

    function onMouseDown(e: MouseEvent) {
        window.removeEventListener('mousemove', calcPosition);
        window.removeEventListener('mouseup', onMouseUp);

        window.addEventListener('mousemove', calcPosition);
        window.addEventListener('mouseup', onMouseUp);

        selfRef.current?.classList.add('pressed');

        const selfRect = (selfRef.current as HTMLElement).getBoundingClientRect();
        const offsetSize = sizeOffsets[size];

        let currentProgress = e[mouseOrientations[orientation]] - selfRect[positionOrientations[orientation]] - offsetSize;
        if (orientation === SliderOrientation.VERTICAL) currentProgress = (selfRect.height - offsetSize * 2) - currentProgress;

        progress.current.currentHandle = isRange ? Math.abs(currentProgress - progress.current.handleMin) < Math.abs(currentProgress - progress.current.handleMax) ? 'handleMin' : 'handleMax' : 'handleMax';
        calcPosition(e);
    }

    function onMouseUp(e: MouseEvent) {
        selfRef.current?.classList.remove('pressed');
        window.removeEventListener('mousemove', calcPosition);
        window.removeEventListener('mouseup', onMouseUp);
    }

    return (
        <Wrapper className={`orientation-${orientation} ${className || ''}`} size={size} orientation={orientation} ref={selfRef} onMouseDown={onMouseDown}>
            <div className='slider-value-container'>
                <div ref={valueTransformRef}>
                    <div ref={valueRef} className='slider-value'></div>
                </div>
            </div>
            <div className='slider-container'>
                <div ref={barRef} className='slider-bar'>
                    <div className={`slider-handle min ${!isRange && 'hidden'}`}></div>
                    <div className='slider-handle max'></div>
                </div>
            </div>
            <div className='slider-marks-container'>
                {marks?.map((mark) => <div className='slider-mark' key={mark.value} style={{ [orientation === SliderOrientation.VERTICAL ? 'bottom' : 'left']: (mark.value - min) / ((max - min) / 100) + '%' }}>{mark.label}</div>)}
            </div>
        </Wrapper>
    )
}

const Wrapper = styled.div<any>`
    user-select: none;

    .slider-value-container {
        display: flex;
        height: 0px;
        pointer-events: none;
    }

    .slider-value {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        padding: 0 10px;
        height: 26px;
        margin-top: -34px;
        background-color: ${props => colorShade(props.theme.backgroundColor, 0.2)};
        border-radius: ${props => props.theme.borderRadius};
        opacity: 0;
        transform: translateY(20px);
        transition: opacity var(--ui-animation-duration), transform var(--ui-animation-duration);
    }
    &.pressed .slider-value { opacity: 1; transform: translateY(0px); }

    .slider-container { background-color: ${props => props.theme.backgroundColor}; }

    .slider-bar {
        background-color: ${props => colorShade(props.theme.primaryColor, -0)};
        
        display: flex;
        justify-content: space-between;
        align-items: center;
        
        transition: opacity var(--ui-animation-duration), background-color var(--ui-animation-duration);
        transition-timing-function: linear;
    }

    .slider-handle {
        width: 4px;
        height: 10px;
        margin: 0 -2px;
        /* background-color: ${props => colorShade(props.theme.primaryColor, -0.2)}; */
        /* background-color: ${props => colorShade(props.theme.primaryColor, 0.8)}; */
        /* background-color: ${props => colorShade(props.theme.primaryColor, -0.4)}; */
        /* background-color: ${props => colorShade(props.theme.backgroundColor, 0)}; */
        background-color: #00000077;
        /* background-color: ${props => colorShade(props.theme.backgroundColor, 0.1)}; */
        border-radius: 5px;
        flex-shrink: 0;

        opacity: 0;
        transform: scale(0);
        transition: opacity var(--ui-animation-duration), transform var(--ui-animation-duration);

        &.hidden { visibility: hidden; }
    }
    &.pressed .slider-handle { opacity: 1; transform: scale(1); }

    .slider-marks-container { position: relative; }

    .slider-mark {
        transform: translateX(-50%);
        position: absolute;
        font-size: 11px;
        font-weight: 700;
        color: #ffffff;
        white-space: nowrap;
    }

    &:hover .slider-handle { opacity: 1; transform: scale(1) }
    &:hover .slider-value { opacity: 1; transform: translateY(0)}

    ${({ size }) => sizes[size as SliderSize]}
    ${({ orientation }) => orientations[orientation as SliderOrientation]}
`

const sizes = {
    [SliderSize.SMALL]: css`
        padding-top: 15px;
        height: 40px;
        .slider-container { border-radius: 5px; }
        &.orientation-0 .slider-bar { height: 10px; border-radius: 5px; padding: 0 5px; }
        &.orientation-0 .slider-marks-container { margin: 0 5px; margin-top: 2px; }
        &.orientation-0 .slider-handle { width: 20px; height: 20px; border-radius: 10px; background-color: #ffffff33; margin: 0 -10px; }
        &.orientation-0 .slider-value { padding: 0 10px; height: 20px; margin-top: -34px; }

        &.orientation-1 .slider-container { width: 10px }
        &.orientation-1 .slider-bar { width: 10px; border-radius: 5px; padding: 5px 0; }
        &.orientation-1 .slider-marks-container { margin: 5px 0; margin-left: 3px; }
        &.orientation-1 .slider-handle { width: 20px; height: 20px; border-radius: 10px; background-color: #ffffff33; margin: -10px 0; }
        &.orientation-1 .slider-value { padding: 0 10px; height: 20px; bottom: -10px; }
    `,
    [SliderSize.MEDIUM]: css`
        padding-top: 15px;
        height: 50px;
        .slider-container { border-radius: 10px; }
        &.orientation-0 .slider-bar { height: 20px; border-radius: 10px; padding: 0 10px; }
        &.orientation-0 .slider-marks-container { margin: 0 10px; margin-top: 2px; }

        &.orientation-1 .slider-container { width: 20px }
        &.orientation-1 .slider-bar { width: 20px; border-radius: 10px; padding: 10px 0; }
        &.orientation-1 .slider-marks-container { margin: 10px 0; margin-left: 4px; }
    `,
    [SliderSize.LARGE]: css`
        padding-top: 15px;
        height: 60px;
        .slider-container { border-radius: 16px; }
        &.orientation-0 .slider-bar { height: 30px; border-radius: 15px; padding: 0 15px; }
        &.orientation-0 .slider-marks-container { margin: 0 15px; margin-top: 3px; }

        &.orientation-1 .slider-container { width: 30px }
        &.orientation-1 .slider-bar { width: 30px; border-radius: 15px; padding: 15px 0; }
        &.orientation-1 .slider-marks-container { margin: 15px 0; margin-left: 4px; }
    `,
}

const orientations = {
    [SliderOrientation.HORIZONTAL]: css``,
    [SliderOrientation.VERTICAL]: css`
        height: 100%;
        min-height: 180px;
        display: flex;
        align-items: stretch;
        justify-content: center;
        padding-top: 0;

        .slider-value-container { width: 0; height: unset; position: relative; }
        .slider-value { position: absolute; bottom: -13px; right: 10px; margin-top: unset; white-space: nowrap; }
        .slider-handle { width: 10px; height: 4px; margin: -2px 0; }
        .slider-container { height: 100%; display: flex; align-items: flex-end; }
        .slider-bar { flex-direction: column; flex-flow: column-reverse; padding: 10px 0px; width: 20px}
        /* .slider-marks-container { height: 100%; } */
        .slider-mark { transform: translateY(50%); }
    `,
}